import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PackagePlus, Mail, Inbox, CheckCircle2, RotateCcw, Trash2, Rocket, BadgeCheck, Lock, Check, X, Pencil } from "lucide-react";
import api, { mediaUrl, listingPriceParts } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import ListingEditModal from "@/components/ListingEditModal";
import { toast } from "sonner";

const PLACEHOLDER = "https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?crop=entropy&cs=srgb&fm=jpg&q=80&w=400";
const statusMap = {
  pending: { label: "În așteptare", cls: "bg-amber-500" },
  approved: { label: "Aprobat", cls: "bg-green-600" },
  rejected: { label: "Respins", cls: "bg-red-600" },
  sold: { label: "Vândut / Inactiv", cls: "bg-slate-500" },
};

export default function MyListings() {
  const [listings, setListings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [params, setParams] = useSearchParams();
  const polled = useRef(false);
  const { t } = useTranslation();

  const load = () => {
    Promise.all([
      api.get("/my-listings").then((r) => setListings(r.data)).catch(() => {}),
      api.get("/my-listing-messages").then((r) => setMessages(r.data)).catch(() => {}),
      api.get("/investment-requests/incoming").then((r) => setIncoming(r.data)).catch(() => {}),
      api.get("/investment-requests/outgoing").then((r) => setOutgoing(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const REQ_TYPE_LABEL = { investment: t("capital.reqInvestment"), apprenticeship: t("capital.reqApprenticeship"), donation: t("capital.reqDonation") };
  const REQ_STATUS = {
    pending: { label: "În așteptare", cls: "bg-amber-500" },
    approved: { label: "Aprobat", cls: "bg-green-600" },
    rejected: { label: "Respins", cls: "bg-red-600" },
  };

  const decideRequest = async (rid, action) => {
    try {
      await api.post(`/investment-requests/${rid}/${action}`);
      toast.success(action === "approve" ? "Cerere aprobată — solicitantul vede acum datele complete." : "Cerere respinsă.");
      load();
    } catch { toast.error("Eroare. Încearcă din nou."); }
  };

  // Handle return from Gold VIP boost Stripe checkout
  useEffect(() => {
    const boostSession = params.get("boost");
    if (!boostSession || polled.current) return;
    polled.current = true;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const st = await api.get(`/checkout/status/${boostSession}`);
        if (st.data.payment_status === "paid") { toast.success("Anunț promovat Gold VIP! Badge „AI Verified Price\" activ."); setParams({}); load(); return; }
        if (st.data.status === "expired" || attempts >= 6) { setParams({}); return; }
        setTimeout(poll, 2000);
      } catch { setParams({}); }
    };
    poll();
    // eslint-disable-next-line
  }, [params]);

  const boost = async (id) => {
    try {
      const { data } = await api.post(`/listings/${id}/boost-checkout`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch { toast.error("Eroare la inițierea plății."); }
  };

  const markSold = async (id) => {
    if (!window.confirm("Sigur doriți să eliminați acest anunț din piață? Va fi marcat ca vândut și nu va mai fi vizibil public.")) return;
    try { await api.post(`/listings/${id}/sold`); toast.success("Anunț marcat ca vândut — eliminat din piață."); load(); }
    catch { toast.error("Eroare. Încearcă din nou."); }
  };
  const reactivate = async (id) => {
    if (!window.confirm("Reactivezi acest anunț? Va redeveni vizibil public.")) return;
    try { await api.post(`/listings/${id}/reactivate`); toast.success("Anunț reactivat."); load(); }
    catch { toast.error("Eroare. Încearcă din nou."); }
  };
  const remove = async (id) => {
    if (!window.confirm("Sigur doriți să eliminați definitiv acest anunț? Acțiunea nu poate fi anulată.")) return;
    try { await api.delete(`/listings/${id}`); toast.success("Anunț șters definitiv."); load(); }
    catch { toast.error("Eroare. Încearcă din nou."); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="my-listings-page">
      <div className="flex items-center justify-between gap-4 mb-10 flex-wrap">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Anunțurile mele</h1>
        <Link to="/vinde" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="new-listing-button">
          <PackagePlus className="w-4 h-4" /> Anunț nou
        </Link>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400">Se încarcă...</div>
      ) : listings.length === 0 ? (
        <div className="border border-slate-200 p-12 text-center" data-testid="no-listings">
          <p className="text-slate-500">Nu ai niciun anunț încă.</p>
          <Link to="/vinde" className="inline-block mt-4 text-orange-600 font-semibold hover:underline">Publică o mașină</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {listings.map((l) => {
            const st = statusMap[l.status] || statusMap.pending;
            const msgs = messages.filter((m) => m.listing_id === l.id);
            return (
              <div key={l.id} className="border border-slate-200 p-5 flex flex-col sm:flex-row gap-5" data-testid={`my-listing-${l.id}`}>
                <img src={mediaUrl(l.image) || PLACEHOLDER} alt={l.name} onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} className="w-full sm:w-32 h-32 object-cover bg-slate-100 border border-slate-200" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-bold text-slate-900 flex items-center gap-2">{l.name}{l.ai_verified_price && <span className="inline-flex items-center gap-1 bg-amber-500 text-slate-900 text-[10px] font-bold uppercase px-1.5 py-0.5" data-testid={`vip-badge-${l.id}`}><BadgeCheck className="w-3 h-3" /> AI Verified</span>}</h3>
                    <Badge className={`rounded-none ${st.cls}`} data-testid={`listing-status-${l.id}`}>{st.label}</Badge>
                  </div>
                  <div className="text-lg font-black mt-1">{(() => { const p = listingPriceParts(l); return (<>{p.main}{p.eur ? <span className="text-xs text-slate-400 font-mono ml-2">{p.eur}</span> : null}</>); })()}</div>
                  {l.status === "rejected" && l.reject_reason && (
                    <p className="text-sm text-red-600 mt-2">Motiv respingere: {l.reject_reason}</p>
                  )}
                  {l.status === "approved" && (
                    <Link to={`/anunt/${l.id}`} className="text-sm text-orange-600 font-semibold hover:underline mt-1 inline-block">Vezi anunțul public →</Link>
                  )}

                  {/* Seller self-service actions */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button onClick={() => setEditing(l)} className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-orange-600 hover:text-orange-600 px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors" data-testid={`edit-listing-${l.id}`}>
                      <Pencil className="w-3.5 h-3.5" /> Editează
                    </button>
                    {l.status === "approved" && (
                      <button onClick={() => markSold(l.id)} className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors" data-testid={`mark-sold-${l.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> Marchează ca vândut
                      </button>
                    )}
                    {l.status === "approved" && !l.vip && (
                      <button onClick={() => boost(l.id)} className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-slate-900 px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors" data-testid={`boost-${l.id}`}>
                        <Rocket className="w-3.5 h-3.5" /> Gold VIP · 90 RON + TVA
                      </button>
                    )}
                    {l.status === "sold" && (
                      <button onClick={() => reactivate(l.id)} className="inline-flex items-center gap-1.5 border border-slate-300 hover:border-slate-900 px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors" data-testid={`reactivate-${l.id}`}>
                        <RotateCcw className="w-3.5 h-3.5" /> Reactivează
                      </button>
                    )}
                    <button onClick={() => remove(l.id)} className="inline-flex items-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors" data-testid={`delete-listing-${l.id}`}>
                      <Trash2 className="w-3.5 h-3.5" /> Șterge
                    </button>
                  </div>

                  {msgs.length > 0 && (
                    <div className="mt-3 border-t border-slate-100 pt-3">
                      <div className="text-xs uppercase tracking-wide font-bold text-slate-500 flex items-center gap-2 mb-2"><Inbox className="w-3.5 h-3.5" /> Mesaje primite ({msgs.length})</div>
                      <div className="space-y-2">
                        {msgs.map((m) => (
                          <div key={m.id} className="bg-slate-50 p-3 text-sm" data-testid={`listing-message-${m.id}`}>
                            <div className="font-semibold flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-orange-600" /> {m.name} <span className="font-normal text-slate-400">· {m.email}{m.phone ? ` · ${m.phone}` : ""}</span></div>
                            <p className="text-slate-600 mt-1">{m.message}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* OXO Capital — incoming access requests (owner) */}
      {incoming.length > 0 && (
        <div className="mt-12" data-testid="incoming-requests">
          <h2 className="text-xl font-black tracking-tight flex items-center gap-2 mb-4"><Lock className="w-5 h-5 text-indigo-600" /> {t("capital.incomingTitle")}</h2>
          <div className="space-y-3">
            {incoming.map((r) => {
              const st = REQ_STATUS[r.status] || REQ_STATUS.pending;
              return (
                <div key={r.id} className="border border-slate-200 p-4" data-testid={`incoming-request-${r.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-bold text-slate-900">{r.requester_name} <span className="font-normal text-slate-400 text-sm">· {r.requester_email}</span></div>
                      <div className="text-sm text-slate-500 mt-0.5">Anunț: <Link to={`/anunt/${r.listing_id}`} className="text-orange-600 hover:underline">{r.listing_title}</Link></div>
                      <div className="mt-1 inline-flex items-center gap-2">
                        <span className="bg-indigo-100 text-indigo-700 text-[11px] font-bold uppercase tracking-wide px-2 py-0.5">{REQ_TYPE_LABEL[r.request_type] || r.request_type}</span>
                        <Badge className={`rounded-none ${st.cls}`} data-testid={`request-status-${r.id}`}>{st.label}</Badge>
                      </div>
                      {r.message && <p className="text-sm text-slate-600 mt-2">{r.message}</p>}
                    </div>
                    {r.status === "pending" && (
                      <div className="flex gap-2">
                        <button onClick={() => decideRequest(r.id, "approve")} className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors" data-testid={`approve-request-${r.id}`}>
                          <Check className="w-3.5 h-3.5" /> {t("capital.approve")}
                        </button>
                        <button onClick={() => decideRequest(r.id, "reject")} className="inline-flex items-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors" data-testid={`reject-request-${r.id}`}>
                          <X className="w-3.5 h-3.5" /> {t("capital.reject")}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* OXO Capital — my outgoing access requests */}
      {outgoing.length > 0 && (
        <div className="mt-12" data-testid="outgoing-requests">
          <h2 className="text-xl font-black tracking-tight flex items-center gap-2 mb-4"><Inbox className="w-5 h-5 text-slate-600" /> {t("capital.outgoingTitle")}</h2>
          <div className="space-y-3">
            {outgoing.map((r) => {
              const st = REQ_STATUS[r.status] || REQ_STATUS.pending;
              return (
                <div key={r.id} className="border border-slate-200 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`outgoing-request-${r.id}`}>
                  <div>
                    <Link to={`/anunt/${r.listing_id}`} className="font-bold text-slate-900 hover:text-orange-600">{r.listing_title}</Link>
                    <div className="text-sm text-slate-500 mt-0.5">{REQ_TYPE_LABEL[r.request_type] || r.request_type}</div>
                  </div>
                  <Badge className={`rounded-none ${st.cls}`} data-testid={`my-request-status-${r.id}`}>{st.label}</Badge>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {editing && (
        <ListingEditModal listing={editing} onClose={() => setEditing(null)} onSaved={load} />
      )}
    </div>
  );
}
